<template>
  <div :id="id" class="text-white"/>
</template>

<script setup>
import Highcharts from "@/highcharts"
import { chartColors } from "@/settings"
import { computed, onMounted } from "vue"

const props = defineProps({
  school: { type: Object, required: true },
})

const id = computed(() => `employer-drilldown-${props.school.id}`)

onMounted(() => {
  const school = props.school.latest_employment_aba
  const { emp, grads, emp_unknown_LT_PT, emp_unknown_ST_FT, emp_unknown_ST_PT } = school
  const other_employed_PT_or_ST = school.govt_LT_PT + school.govt_ST_FT +
    school.govt_ST_PT + school.pubInt_LT_PT + school.pubInt_ST_FT +
    school.pubInt_ST_PT + school.firm_LT_PT + school.firm_ST_FT +
    school.firm_ST_PT + school.federal_LT_PT + school.federal_ST_FT +
    school.federal_ST_PT + school.state_LT_PT + school.state_ST_FT +
    school.state_ST_PT + school.otherClerk_LT_PT + school.otherClerk_ST_FT +
    school.otherClerk_ST_PT + school.academic_LT_PT + school.academic_ST_FT +
    school.academic_ST_PT + school.business_LT_PT + school.business_ST_FT
    + school.business_ST_PT + emp_unknown_LT_PT + emp_unknown_ST_FT
    + emp_unknown_ST_PT

  const series = [
    {
      name: "Non-employed",
      data: [{
        name: "Employer types",
        y: percent((grads - emp), grads, "", ""),
        drilldown: "non",
        color: chartColors.nonEmployed,
      }],
    },
    {
      name: "Employed,<br>other jobs",
      data: [{
        name: "Employer types",
        y: percent((other_employed_PT_or_ST + school.business_LT_FT + school.academic_LT_FT + school.firm_unk_LT_FT), grads, "", ""),
        drilldown: "other",
        color: chartColors.otherJobs,
      }],
    },
    {
      name: "Small firm",
      data: [{
        name: "Employer types",
        y: percent(["firm_solo_LT_FT", "firm_2_10_LT_FT"], grads),
        drilldown: "smalllaw",
        color: chartColors.smallFirm,
      }],
    },
    {
      name: "Regional firm",
      data: [{
        name: "Employer types",
        y: percent(["firm_101_250_LT_FT", "firm_11_25_LT_FT", "firm_26_50_LT_FT", "firm_51_100_LT_FT"], grads),
        drilldown: "regional",
        color: chartColors.regionalFirm,
      }],
    },
    {
      name: "National firm",
      data: [{
        name: "Employer types",
        y: percent(["firm_251_500_LT_FT", "firm_501_LT_FT"], grads),
        drilldown: "biglaw",
        color: chartColors.largeFirm,
      }],
    },
    {
      name: "Judicial clerkship",
      data: [{
        name: "Employer types",
        y: percent(["federal_LT_FT", "state_LT_FT", "otherClerk_LT_FT", "tribal_LT_FT", "intl_LT_FT"], grads),
        drilldown: "jc",
        color: chartColors.judicialClerkship,
      }],
    },
    {
      name: "Public service",
      data: [{
        name: "Employer types",
        y: percent(["pubInt_LT_FT", "govt_LT_FT"], grads),
        drilldown: "ps",
        color: chartColors.publicService,
      }],
    },
  ]

  Highcharts.chart(id.value, {
    chart: {
      type: "bar", spacing: [0, 0, 0, 0], margin: [0, 0, 0, 0],
      width: 500,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "category", minPadding: 0, maxPadding: 0,
      labels: { enabled: true },
      startOnTick: true,
    },
    yAxis: {
      title: { enabled: false }, labels: { enabled: false },
      max: 100, min: 0, gridLineWidth: 0, minPadding: 0, maxPadding: 0,
    },
    tooltip: {
      backgroundColor: "rgba(255,255,255,.95)",
      formatter() {
        if (this.point.drilldown === "biglaw" || this.point.drilldown === "smalllaw") {
          if (this.y >= 12) {
            return false
          }

          return `${this.series.name}: ${this.y}%`
        } else if (this.y >= 12) {
          return "Click to zoom"
        }

        return `${this.series.name}: ${this.y}%` + "<br>Click to zoom"
      },
    },
    plotOptions: {
      series: {
        states: { hover: { enabled: true }, inactive: { opacity: 1 } },
        borderWidth: 0,
        stacking: "normal",
        groupPadding: 0,
      },
      bar: {
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: 0,
          allowOverlap: false,
          style: { fontSize: "13px", textOutline: false, fontWeight: "500" },
          crop: false,
          formatter() {
            if (this.point.drilldown) {
              if (this.y < 12) {
                return null
              }

              return `${this.series.name}<br>${this.y}%`
            }

            return `${this.point.name}: ${this.point.y}%`
          },
          color: (Highcharts.theme && Highcharts.theme.dataLabelsColor) || "black",
        },
      },
    },
    credits: { enabled: false },
    legend: { enabled: false },
    series,
    drilldown: {
      activeDataLabelStyle: {
        color: "white",
        textShadow: "0 0 1px black, 0 0 1px black",
        textDecoration: "none",
      },
      series: [{
        id: "ps",
        name: "Types of public service",
        data: [
          ["Government", percent(["govt_LT_FT"], grads, school, "")],
          ["Public Interest Organization", percent(["pubInt_LT_FT"], grads, school, "")],
        ],
      }, {
        id: "jc",
        name: "Types of judicial clerkships",
        data: [
          ["Federal", percent(["federal_LT_FT"], grads, school, "")],
          ["State", percent(["state_LT_FT"], grads, school, "")],
          ["Other", percent(["otherClerk_LT_FT", "tribal_LT_FT", "intl_LT_FT"], grads, school, "")],
        ],
      }, {
        id: "regional",
        name: "Regional firm size",
        data: [
          ["Small regional firm", percent(["firm_11_25_LT_FT", "firm_26_50_LT_FT", "firm_51_100_LT_FT"], grads, school, "")],
          ["Large regional firm", percent(["firm_101_250_LT_FT"], grads, school, "")],
        ],
      }, {
        id: "other",
        name: "Other Jobs",
        data: [
          ["Business", percent(["business_LT_FT"], grads, school, "")],
          ["Education", percent(["academic_LT_FT"], grads, school, "")],
          ["Firm, Unknown Size", percent(["firm_unk_LT_FT"], grads, school, "")],
          ["Short Term or Part Time Job", percent(other_employed_PT_or_ST, grads, "", "")],
        ],
      }, {
        id: "non",
        name: "Non-employed",
        data: [
          ["Pursuing another degree", percent(["FTDegree"], grads, school, "")],
          ["Unemployed", percent(["unemp_def", "unemp_not_seek", "unemp_seek"], grads, school, "")],
          ["Unknown status", percent(["unk"], grads, school, "")],
        ],
      }],
    },
  })
})

const percent = (num, denom) => {
  let sum = num

  if (Array.isArray(num)) {
    sum = num.reduce((partialSum, key) => partialSum + props.school.latest_employment_aba[key], 0)
  }

  if (denom === 0) {
    return 0
  }

  return Math.round((sum / denom) * 1000) / 10
}
</script>
